import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Row,
  Col,
  Select,
  Spin,
  Typography,
  Tooltip,
  Space,
  Button,
  Empty,
  Badge,
  Avatar,
  Table,
} from "antd";
import {
  BarChartOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  UserOutlined,
  FileTextOutlined,
  RiseOutlined,
  FallOutlined,
  ClockCircleOutlined,
  MailOutlined,
  LinkedinOutlined,
  ChromeOutlined,
  MobileOutlined,
  DesktopOutlined,
  ApiOutlined,
  MessageOutlined,
  EditOutlined,
  RobotOutlined,
  FireOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  TrophyOutlined,
  GlobalOutlined,
  LaptopOutlined,
  PieChartOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  getDashboardOverview,
  getTimeSeries,
  getPlatformDistribution,
  getProviderBreakdown,
  getUserActivity,
  getRealtimeEvents,
  getFeatureUsage,
  getGeography,
  getEventBreakdown,
  getDeviceAnalytics,
} from "@api/analytics";

const { Text } = Typography;
const { Option } = Select;

// Card wrapper component (same as VisitorAnalytics)
const ChartCard = ({ title, subtitle, badge, children, minHeight = 300 }) => (
  <div style={{
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #f1f5f9",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    height: "100%",
  }}>
    <div style={{
      marginBottom: "20px",
      paddingBottom: "16px",
      borderBottom: "1px solid #f1f5f9",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
    }}>
      <div>
        <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: 0 }}>
          {title}
        </h3>
        {subtitle && (
          <p style={{ fontSize: "13px", color: "#64748b", margin: "4px 0 0" }}>
            {subtitle}
          </p>
        )}
      </div>
      {badge !== undefined && (
        <Badge count={badge} style={{ backgroundColor: "#8b5cf6" }} overflowCount={999} />
      )}
    </div>
    <div style={{ minHeight }}>{children}</div>
  </div>
);

// Metric card component (same as VisitorAnalytics)
const MetricCard = ({ title, value, icon, color, trend }) => {
  const colorMap = {
    blue: { bg: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", text: "#1d4ed8", gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)" },
    green: { bg: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", text: "#059669", gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
    purple: { bg: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)", text: "#7c3aed", gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" },
    orange: { bg: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", text: "#d97706", gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" },
    pink: { bg: "linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)", text: "#db2777", gradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)" },
    red: { bg: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)", text: "#dc2626", gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" },
    teal: { bg: "linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)", text: "#0d9488", gradient: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div style={{
      background: c.bg,
      borderRadius: "16px",
      padding: "20px",
      border: "1px solid rgba(0,0,0,0.06)",
      minHeight: "120px",
    }}>
      <div style={{
        width: "44px",
        height: "44px",
        borderRadius: "12px",
        background: c.gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: "18px",
        marginBottom: "14px",
      }}>
        {icon}
      </div>
      <div style={{ fontSize: "28px", fontWeight: 700, color: c.text, lineHeight: 1.1 }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div style={{ fontSize: "13px", fontWeight: 500, color: "#64748b", marginTop: "4px" }}>
        {title}
      </div>
      {trend !== undefined && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          marginTop: "8px",
          fontSize: "12px",
          fontWeight: 600,
          color: trend >= 0 ? "#059669" : "#dc2626",
        }}>
          {trend >= 0 ? <RiseOutlined /> : <FallOutlined />}
          <span>{trend >= 0 ? "+" : ""}{trend.toFixed(1)}%</span>
        </div>
      )}
    </div>
  );
};

// Format large numbers
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num?.toLocaleString() || 0;
};

// Provider icons
const getProviderIcon = (provider) => {
  const icons = {
    gmail: <MailOutlined />,
    outlook: <MailOutlined />,
    linkedin: <LinkedinOutlined />,
    default: <MailOutlined />,
  };
  return icons[provider?.toLowerCase()] || icons.default;
};

// Platform icons
const getPlatformIcon = (platform) => {
  const icons = {
    "browser extension": <ChromeOutlined />,
    "web app": <DesktopOutlined />,
    "mobile app": <MobileOutlined />,
    "desktop app": <DesktopOutlined />,
    "api": <ApiOutlined />,
  };
  return icons[platform?.toLowerCase()] || <DesktopOutlined />;
};

// Event type config - matches tracked event names
const eventTypeConfig = {
  // Smart Reply events
  smart_reply: { label: "Smart Reply", color: "#3b82f6", icon: <MessageOutlined /> },
  "smart_reply.generated": { label: "Smart Reply Generated", color: "#3b82f6", icon: <MessageOutlined /> },
  "smart_reply.modal_opened": { label: "Smart Reply Modal", color: "#3b82f6", icon: <MessageOutlined /> },
  "smart_reply.inserted": { label: "Smart Reply Inserted", color: "#3b82f6", icon: <MessageOutlined /> },
  // Chat events
  chat: { label: "Chat", color: "#10b981", icon: <MessageOutlined /> },
  "chat.created": { label: "Chat Created", color: "#10b981", icon: <MessageOutlined /> },
  "chat.message_sent": { label: "Chat Message", color: "#10b981", icon: <MessageOutlined /> },
  // Copilot events
  copilot: { label: "Copilot", color: "#8b5cf6", icon: <RobotOutlined /> },
  "copilot.selected": { label: "Copilot Selected", color: "#8b5cf6", icon: <RobotOutlined /> },
  "copilot.generation_started": { label: "Generation Started", color: "#8b5cf6", icon: <RobotOutlined /> },
  "copilot.generation_completed": { label: "Generation Completed", color: "#8b5cf6", icon: <RobotOutlined /> },
  // Quick Copilot events
  quick_copilot: { label: "Quick Copilot", color: "#7c3aed", icon: <RobotOutlined /> },
  "quick_copilot.used": { label: "Quick Copilot Used", color: "#7c3aed", icon: <RobotOutlined /> },
  "quick_copilot.created": { label: "Quick Copilot Created", color: "#7c3aed", icon: <RobotOutlined /> },
  // Craft events
  craft: { label: "Craft", color: "#f59e0b", icon: <EditOutlined /> },
  "craft.started": { label: "Craft Started", color: "#f59e0b", icon: <EditOutlined /> },
  "craft.completed": { label: "Craft Completed", color: "#f59e0b", icon: <EditOutlined /> },
  // Widget events
  widget: { label: "Widget", color: "#06b6d4", icon: <DesktopOutlined /> },
  "widget.opened": { label: "Widget Opened", color: "#06b6d4", icon: <DesktopOutlined /> },
  "widget.closed": { label: "Widget Closed", color: "#64748b", icon: <DesktopOutlined /> },
  "widget.tab_changed": { label: "Widget Tab Changed", color: "#06b6d4", icon: <DesktopOutlined /> },
  // Generation events
  generation: { label: "Generation", color: "#ec4899", icon: <FileTextOutlined /> },
};

// Helper to get event config with fallback
const getEventConfig = (eventName) => {
  // Direct match
  if (eventTypeConfig[eventName]) {
    return eventTypeConfig[eventName];
  }
  // Check prefix match (e.g., "smart_reply.generated" -> "smart_reply")
  const prefix = eventName?.split(".")[0];
  if (prefix && eventTypeConfig[prefix]) {
    return { ...eventTypeConfig[prefix], label: eventName.replace(/[._]/g, " ").replace(/\b\w/g, c => c.toUpperCase()) };
  }
  // Default fallback
  return { label: eventName || "Unknown", color: "#64748b", icon: <FileTextOutlined /> };
};

const AnalyticsDashboard = ({ showHeader = true }) => {
  const history = useHistory();
  const location = useLocation();

  const getInitialDateRange = () => {
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get("dateRange");
    const validDateRanges = [7, 14, 30, 90, 365, 9999];
    const parsedDate = parseInt(dateParam);
    return validDateRanges.includes(parsedDate) ? parsedDate : 14;
  };

  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get("tab");
    const validTabs = ["overview", "events", "users", "devices", "geographic", "realtime"];
    return validTabs.includes(tabParam) ? tabParam : "overview";
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Tab configuration
  const tabs = [
    { key: "overview", label: "Overview", icon: <TrophyOutlined /> },
    { key: "events", label: "Events", icon: <BarChartOutlined /> },
    { key: "users", label: "Users", icon: <TeamOutlined /> },
    { key: "devices", label: "Devices", icon: <LaptopOutlined /> },
    { key: "geographic", label: "Geographic", icon: <GlobalOutlined /> },
    { key: "realtime", label: "Realtime", icon: <ThunderboltOutlined /> },
  ];

  // Overview Query - Always enabled for metrics
  const { data: overview, isLoading: overviewLoading, refetch } = useQuery(
    ["analyticsOverview", dateRange],
    async () => {
      const res = await getDashboardOverview({ days: dateRange });
      return res.data;
    },
    { staleTime: 60000, keepPreviousData: true }
  );

  // Time Series Query
  const { data: timeSeries = { labels: [], datasets: [] } } = useQuery(
    ["analyticsTimeSeries", dateRange],
    async () => {
      const res = await getTimeSeries({
        days: dateRange,
        metric: "generations",
        granularity: dateRange <= 7 ? "hour" : dateRange <= 30 ? "day" : "week",
      });
      return res.data || { labels: [], datasets: [] };
    },
    { enabled: activeTab === "overview", staleTime: 60000, keepPreviousData: true }
  );

  // Platform Distribution Query
  const { data: platforms = [] } = useQuery(
    ["analyticsPlatforms", dateRange],
    async () => {
      const res = await getPlatformDistribution({ days: dateRange });
      return res.data || [];
    },
    { enabled: activeTab === "overview" || activeTab === "devices", staleTime: 60000, keepPreviousData: true }
  );

  // Provider Breakdown Query (used as event types)
  const { data: providers = [] } = useQuery(
    ["analyticsProviders", dateRange],
    async () => {
      const res = await getProviderBreakdown({ days: dateRange });
      return res.data || [];
    },
    { enabled: activeTab === "overview", staleTime: 60000, keepPreviousData: true }
  );

  // User Activity Query
  const { data: userActivity } = useQuery(
    ["analyticsUserActivity", dateRange],
    async () => {
      const res = await getUserActivity({ days: dateRange }, { page: 1, perPage: 8 });
      return res.data || { users: [], total: 0 };
    },
    { enabled: activeTab === "overview" || activeTab === "users", staleTime: 60000, keepPreviousData: true }
  );

  // Feature Usage Query
  const { data: features = [] } = useQuery(
    ["analyticsFeatures", dateRange],
    async () => {
      const res = await getFeatureUsage({ days: dateRange });
      return res.data || [];
    },
    { enabled: activeTab === "overview" || activeTab === "events", staleTime: 60000, keepPreviousData: true }
  );

  // Event Breakdown Query
  const { data: eventBreakdown } = useQuery(
    ["analyticsEventBreakdown", dateRange],
    async () => {
      const res = await getEventBreakdown({ days: dateRange });
      return res.data || { events: [], categories: [], total: 0 };
    },
    { enabled: activeTab === "events", staleTime: 60000, keepPreviousData: true }
  );

  // Geographic Query
  const { data: geoData = [] } = useQuery(
    ["analyticsGeography", dateRange],
    async () => {
      const res = await getGeography({ days: dateRange });
      return res.data || [];
    },
    { enabled: activeTab === "geographic", staleTime: 60000, keepPreviousData: true }
  );

  // Device Analytics Query
  const { data: deviceData } = useQuery(
    ["analyticsDevices", dateRange],
    async () => {
      const res = await getDeviceAnalytics({ days: dateRange });
      return res.data || { browsers: [], operatingSystems: [], deviceTypes: [] };
    },
    { enabled: activeTab === "devices", staleTime: 60000, keepPreviousData: true }
  );

  // Realtime Events Query
  const { data: realtimeEvents = [], refetch: refetchRealtime } = useQuery(
    ["analyticsRealtime"],
    async () => {
      const res = await getRealtimeEvents(20);
      return res.data || [];
    },
    { enabled: activeTab === "overview" || activeTab === "realtime", staleTime: 10000, refetchInterval: activeTab === "realtime" ? 5000 : 15000 }
  );

  const loading = overviewLoading;

  const updateURL = (newDateRange, newTab) => {
    const params = new URLSearchParams(location.search);
    params.set("dateRange", newDateRange.toString());
    params.set("tab", newTab);
    history.replace(`${location.pathname}?${params.toString()}`);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    updateURL(newDateRange, activeTab);
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    updateURL(dateRange, newTab);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const dateParam = urlParams.get("dateRange");
    const tabParam = urlParams.get("tab");

    if (dateParam) {
      const validDateRanges = [7, 14, 30, 90, 365, 9999];
      const parsedDate = parseInt(dateParam);
      if (validDateRanges.includes(parsedDate) && parsedDate !== dateRange) {
        setDateRange(parsedDate);
      }
    }

    if (tabParam) {
      const validTabs = ["overview", "events", "users", "devices", "geographic", "realtime"];
      if (validTabs.includes(tabParam) && tabParam !== activeTab) {
        setActiveTab(tabParam);
      }
    }
  }, [location.search]);

  // Colors for charts
  const colors = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4"];

  // Platform colors
  const platformColors = {
    "Browser Extension": "#8b5cf6",
    "Web App": "#3b82f6",
    "Mobile App": "#10b981",
    "Desktop App": "#f59e0b",
    "API": "#ec4899",
    "Other": "#94a3b8",
  };

  if (loading && !overview) {
    return (
      <div style={{
        background: "#f8fafc",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "16px"
      }}>
        <Spin size="large" />
        <Text style={{ color: "#64748b" }}>Loading analytics data...</Text>
      </div>
    );
  }

  // Process time series data for visualization
  const maxTimeSeriesValue = Math.max(
    ...(timeSeries?.datasets?.[0]?.data || [1]),
    1
  );

  // Process platform data
  const totalPlatformCount = platforms.reduce((sum, p) => sum + (p.count || 0), 0);

  // Process provider data
  const maxProviderCount = Math.max(...providers.map(p => p.count || 0), 1);

  // Process features for event types
  const maxFeatureCount = Math.max(...features.map(f => f.count || 0), 1);

  return (
    <div style={{ background: "#f8fafc", minHeight: showHeader ? "calc(100vh - 80px)" : "auto" }}>
      {/* Hero Header */}
      {showHeader && (
        <div
          style={{
            background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #a78bfa 100%)",
            padding: "32px 24px",
            marginBottom: "24px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{
            position: "absolute",
            top: "-50%",
            right: "-10%",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }} />

          <Row align="middle" justify="space-between" style={{ position: "relative", zIndex: 1, maxWidth: "1400px", margin: "0 auto" }}>
            <Col xs={24} lg={16}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                <div style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "14px",
                  background: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <BarChartOutlined style={{ fontSize: "24px", color: "#fff" }} />
                </div>
                <h1 style={{ fontSize: "28px", fontWeight: 700, color: "#fff", margin: 0 }}>
                  Analytics Dashboard
                </h1>
              </div>
              <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.9)", margin: 0 }}>
                Comprehensive overview of platform performance and user activity
              </p>
            </Col>
            <Col xs={24} lg={8} style={{ textAlign: "right", marginTop: window.innerWidth < 992 ? "20px" : 0 }}>
              <Space size="middle">
                <Select value={dateRange} onChange={handleDateRangeChange} style={{ width: 150 }}>
                  <Option value={7}>Last 7 days</Option>
                  <Option value={14}>Last 14 days</Option>
                  <Option value={30}>Last 30 days</Option>
                  <Option value={90}>Last 90 days</Option>
                  <Option value={365}>Last year</Option>
                  <Option value={9999}>All time</Option>
                </Select>
                <Button
                  icon={<ReloadOutlined spin={loading} />}
                  onClick={() => refetch()}
                  style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff" }}
                />
              </Space>
            </Col>
          </Row>
        </div>
      )}

      {/* Content */}
      <div style={{ padding: showHeader ? "0 24px 24px" : "24px", maxWidth: "1448px", margin: "0 auto" }}>
        {/* Controls when header hidden */}
        {!showHeader && (
          <Row justify="end" style={{ marginBottom: "16px" }}>
            <Space size="middle">
              <Select value={dateRange} onChange={handleDateRangeChange} style={{ width: 150 }}>
                <Option value={7}>Last 7 days</Option>
                <Option value={14}>Last 14 days</Option>
                <Option value={30}>Last 30 days</Option>
                <Option value={90}>Last 90 days</Option>
                <Option value={365}>Last year</Option>
                <Option value={9999}>All time</Option>
              </Select>
              <Button icon={<ReloadOutlined spin={loading} />} onClick={() => refetch()} />
            </Space>
          </Row>
        )}

        {/* Tab Navigation */}
        <div style={{
          display: "flex",
          gap: "8px",
          marginBottom: "24px",
          background: "#fff",
          padding: "8px",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflowX: "auto",
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 16px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
                transition: "all 0.2s",
                whiteSpace: "nowrap",
                background: activeTab === tab.key
                  ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                  : "transparent",
                color: activeTab === tab.key ? "#fff" : "#64748b",
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Metric Cards - Always visible */}
        <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard
              title="Total Events"
              value={formatNumber(overview?.totalEvents || 0)}
              icon={<ThunderboltOutlined />}
              color="blue"
              trend={overview?.growthRate?.events}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard
              title="Active Users"
              value={formatNumber(overview?.activeUsers || 0)}
              icon={<UserOutlined />}
              color="green"
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard
              title="Total Tokens"
              value={formatNumber(overview?.totalTokens || 0)}
              icon={<FireOutlined />}
              color="orange"
              trend={overview?.growthRate?.tokens}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard
              title="Generations"
              value={formatNumber(overview?.totalGenerations || 0)}
              icon={<FileTextOutlined />}
              color="purple"
              trend={overview?.growthRate?.generations}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard
              title="Total Users"
              value={formatNumber(overview?.totalUsers || 0)}
              icon={<UserOutlined />}
              color="pink"
              trend={overview?.growthRate?.users}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <MetricCard
              title="Avg Tokens/Gen"
              value={Math.round(overview?.avgTokensPerGeneration || 0)}
              icon={<BarChartOutlined />}
              color="teal"
            />
          </Col>
        </Row>

        {/* ==================== OVERVIEW TAB ==================== */}
        {activeTab === "overview" && (
          <>
        {/* Feature Usage Chart */}
        <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
          <Col xs={24} lg={16}>
            <ChartCard title="Feature Usage Over Time" subtitle="Activity trends" minHeight={350}>
              {timeSeries?.labels?.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {timeSeries.labels.slice(-14).map((label, index) => {
                    const value = timeSeries.datasets?.[0]?.data?.[index] || 0;
                    const widthPercent = (value / maxTimeSeriesValue) * 100;
                    return (
                      <div key={label} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ width: "80px", fontSize: "12px", color: "#64748b" }}>{label}</div>
                        <div style={{ flex: 1, height: "24px", background: "#f1f5f9", borderRadius: "6px", overflow: "hidden", position: "relative" }}>
                          <div style={{
                            width: `${Math.max(widthPercent, 3)}%`,
                            height: "100%",
                            background: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            paddingRight: widthPercent > 20 ? "8px" : 0
                          }}>
                            {widthPercent > 20 && <span style={{ fontSize: "11px", fontWeight: 600, color: "#fff" }}>{formatNumber(value)}</span>}
                          </div>
                          {widthPercent <= 20 && (
                            <span style={{ position: "absolute", left: `calc(${Math.max(widthPercent, 3)}% + 6px)`, top: "50%", transform: "translateY(-50%)", fontSize: "11px", fontWeight: 600, color: "#64748b" }}>{formatNumber(value)}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
              )}
            </ChartCard>
          </Col>

          <Col xs={24} lg={8}>
            <ChartCard title="Event Types" subtitle="Breakdown by type" badge={features?.length} minHeight={350}>
              {features?.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {features.slice(0, 6).map((feature, index) => {
                    const config = getEventConfig(feature.feature);
                    const widthPercent = (feature.count / maxFeatureCount) * 100;
                    return (
                      <div key={feature.feature} style={{
                        padding: "12px 14px",
                        background: index === 0 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                        border: index === 0 ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                        borderRadius: "10px"
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "8px",
                              background: config.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontSize: "12px"
                            }}>
                              {config.icon}
                            </div>
                            <Text style={{ fontWeight: 600, fontSize: "13px" }}>{config.label}</Text>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: 700, color: config.color, fontSize: "14px" }}>{formatNumber(feature.count)}</div>
                            <div style={{ fontSize: "10px", color: "#94a3b8" }}>{feature.percentage?.toFixed(1) || 0}%</div>
                          </div>
                        </div>
                        <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                          <div style={{ width: `${widthPercent}%`, height: "100%", background: `linear-gradient(90deg, ${config.color} 0%, ${config.color}cc 100%)`, borderRadius: "3px" }} />
                        </div>
                        {feature.trend !== undefined && (
                          <div style={{ marginTop: "6px", fontSize: "11px", color: feature.trend >= 0 ? "#10b981" : "#ef4444", fontWeight: 500 }}>
                            {feature.trend >= 0 ? <RiseOutlined /> : <FallOutlined />} {feature.trend >= 0 ? "+" : ""}{feature.trend}% vs last period
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
              )}
            </ChartCard>
          </Col>
        </Row>

        {/* Platform & Provider Charts */}
        <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
          {/* Platform Distribution */}
          <Col xs={24} lg={12}>
            <ChartCard title="Platform Distribution" subtitle="Usage by platform" badge={platforms?.length} minHeight={320}>
              {platforms?.length > 0 ? (
                <>
                  {/* Donut-like visual */}
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
                    <div style={{ position: "relative", width: "180px", height: "180px" }}>
                      <svg viewBox="0 0 36 36" style={{ transform: "rotate(-90deg)", width: "100%", height: "100%" }}>
                        <circle cx="18" cy="18" r="15.915" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                        {platforms.reduce((acc, platform, index) => {
                          const percentage = totalPlatformCount > 0 ? (platform.count / totalPlatformCount) * 100 : 0;
                          const color = platformColors[platform.platform] || colors[index % colors.length];
                          const offset = acc.offset;
                          acc.elements.push(
                            <circle
                              key={platform.platform}
                              cx="18" cy="18" r="15.915" fill="none"
                              stroke={color} strokeWidth="3"
                              strokeDasharray={`${percentage} ${100 - percentage}`}
                              strokeDashoffset={-offset}
                              strokeLinecap="round"
                            />
                          );
                          acc.offset += percentage;
                          return acc;
                        }, { elements: [], offset: 0 }).elements}
                      </svg>
                      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                        <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e293b" }}>
                          {formatNumber(totalPlatformCount)}
                        </div>
                        <div style={{ fontSize: "11px", color: "#64748b" }}>Total Events</div>
                      </div>
                    </div>
                  </div>
                  {/* Legend */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {platforms.slice(0, 5).map((platform, index) => {
                      const color = platformColors[platform.platform] || colors[index % colors.length];
                      const percentage = totalPlatformCount > 0 ? ((platform.count / totalPlatformCount) * 100).toFixed(1) : 0;
                      return (
                        <div key={platform.platform} style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 12px",
                          background: `${color}10`,
                          borderRadius: "10px",
                          border: `1px solid ${color}20`,
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "8px",
                              background: color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                            }}>
                              {getPlatformIcon(platform.platform)}
                            </div>
                            <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>{platform.platform}</span>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: "14px", fontWeight: 600, color }}>{formatNumber(platform.count)}</div>
                            <div style={{ fontSize: "11px", color: "#64748b" }}>{percentage}%</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
              )}
            </ChartCard>
          </Col>

          {/* Provider Breakdown */}
          <Col xs={24} lg={12}>
            <ChartCard title="Email Provider Breakdown" subtitle="Gmail, Outlook, LinkedIn, etc." badge={providers?.length} minHeight={320}>
              {providers?.length > 0 ? (
                <>
                  {/* Summary stats */}
                  <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
                    <div style={{
                      flex: 1,
                      background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
                      borderRadius: "12px",
                      padding: "14px 16px",
                      textAlign: "center",
                    }}>
                      <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 500, marginBottom: "4px" }}>Total Generations</div>
                      <div style={{ fontSize: "20px", fontWeight: 700, color: "#7c3aed" }}>
                        {formatNumber(providers.reduce((sum, p) => sum + p.count, 0))}
                      </div>
                    </div>
                    <div style={{
                      flex: 1,
                      background: "linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)",
                      borderRadius: "12px",
                      padding: "14px 16px",
                      textAlign: "center",
                    }}>
                      <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 500, marginBottom: "4px", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                        <ThunderboltOutlined style={{ fontSize: "10px" }} /> Total Tokens
                      </div>
                      <div style={{ fontSize: "20px", fontWeight: 700, color: "#d97706" }}>
                        {formatNumber(providers.reduce((sum, p) => sum + (p.tokens || 0), 0))}
                      </div>
                    </div>
                  </div>
                  {/* Provider bars */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {providers.slice(0, 6).map((provider, index) => {
                      const widthPercent = (provider.count / maxProviderCount) * 100;
                      const color = colors[index % colors.length];
                      return (
                        <div key={provider.provider} style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "10px 12px",
                          background: index === 0 ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)" : "#f8fafc",
                          border: index === 0 ? "1px solid #6ee7b7" : "1px solid #e2e8f0",
                          borderRadius: "10px"
                        }}>
                          <div style={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "8px",
                            background: color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: "12px"
                          }}>
                            {getProviderIcon(provider.provider)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                              <Text style={{ fontWeight: 500, fontSize: "13px" }}>{provider.provider}</Text>
                              <Text strong style={{ color }}>{formatNumber(provider.count)}</Text>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: `linear-gradient(90deg, ${color} 0%, ${color}cc 100%)`, borderRadius: "3px" }} />
                            </div>
                          </div>
                          <div style={{ textAlign: "right", minWidth: "50px" }}>
                            <div style={{ fontSize: "10px", color: "#94a3b8" }}>{provider.percentage?.toFixed(1) || 0}%</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
              )}
            </ChartCard>
          </Col>
        </Row>

        {/* User Activity & Recent Activity */}
        <Row gutter={[16, 16]}>
          {/* Top Users Table */}
          <Col xs={24} lg={16}>
            <ChartCard title="Top Active Users" subtitle="Most engaged users" badge={userActivity?.users?.length} minHeight={400}>
              {userActivity?.users?.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {userActivity.users.slice(0, 8).map((user, index) => (
                    <div key={user.id} style={{
                      padding: "12px 14px",
                      background: index < 3 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                      border: index < 3 ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                      borderRadius: "10px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onClick={() => history.push(`/super-admin/admin/${user.id}`)}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <Avatar
                            size={40}
                            src={user.avatar}
                            style={{
                              background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                              fontWeight: 600,
                            }}
                          >
                            {user.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                          </Avatar>
                          <div>
                            <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "14px" }}>
                              {index < 3 && <FireOutlined style={{ color: index === 0 ? "#f59e0b" : index === 1 ? "#94a3b8" : "#cd7f32", marginRight: "6px" }} />}
                              {user.name}
                            </div>
                            <div style={{ fontSize: "12px", color: "#64748b" }}>{user.email}</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "16px", fontWeight: 700, color: "#7c3aed" }}>{formatNumber(user.generations)}</div>
                            <div style={{ fontSize: "10px", color: "#94a3b8" }}>generations</div>
                          </div>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "#f59e0b" }}>{formatNumber(user.tokens)}</div>
                            <div style={{ fontSize: "10px", color: "#94a3b8" }}>tokens</div>
                          </div>
                          <div style={{
                            padding: "4px 10px",
                            background: user.plan === "pro" ? "#8b5cf615" : user.plan === "business" ? "#3b82f615" : "#10b98115",
                            color: user.plan === "pro" ? "#8b5cf6" : user.plan === "business" ? "#3b82f6" : "#10b981",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "capitalize"
                          }}>
                            {user.plan}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
              )}
            </ChartCard>
          </Col>

          {/* Recent Activity Feed */}
          <Col xs={24} lg={8}>
            <ChartCard title="Recent Activity" subtitle="Real-time events" minHeight={400}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
                <Tooltip title="Refresh">
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => refetchRealtime()}
                    style={{ borderRadius: "6px" }}
                  />
                </Tooltip>
              </div>
              {realtimeEvents?.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                  {realtimeEvents.slice(0, 8).map((event, index) => {
                    const config = getEventConfig(event.type);
                    const timeAgo = (() => {
                      const diff = Math.floor((Date.now() - new Date(event.timestamp).getTime()) / 1000);
                      if (diff < 60) return `${diff}s ago`;
                      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                      return `${Math.floor(diff / 86400)}d ago`;
                    })();
                    return (
                      <div key={event.id} style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                        padding: "12px 0",
                        borderBottom: index < realtimeEvents.length - 1 ? "1px solid #f1f5f9" : "none",
                      }}>
                        <div style={{ position: "relative" }}>
                          <Avatar
                            size={36}
                            src={event.user?.avatar}
                            style={{
                              background: "linear-gradient(135deg, #e9d5ff 0%, #f5f3ff 100%)",
                              color: "#7c3aed",
                              fontWeight: 600,
                              fontSize: "12px",
                            }}
                          >
                            {event.user?.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                          </Avatar>
                          <div style={{
                            position: "absolute",
                            bottom: -2,
                            right: -2,
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            background: config.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            border: "2px solid #fff",
                            fontSize: "8px",
                          }}>
                            {config.icon}
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                            <span style={{ fontWeight: 500, fontSize: "13px", color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {event.user?.name}
                            </span>
                            <span style={{ fontSize: "11px", color: "#94a3b8", whiteSpace: "nowrap" }}>{timeAgo}</span>
                          </div>
                          <div style={{ fontSize: "12px", color: config.color, fontWeight: 500, marginBottom: "2px" }}>
                            {config.label}
                          </div>
                          {event.metadata?.tokens && (
                            <span style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              background: "#fef3c7",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              color: "#d97706",
                              fontSize: "10px",
                              fontWeight: 500,
                            }}>
                              <ThunderboltOutlined style={{ fontSize: "8px" }} />
                              {event.metadata.tokens.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent activity" />
              )}
              {/* Live indicator */}
              <div style={{
                marginTop: "12px",
                padding: "8px",
                borderTop: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                fontSize: "11px",
                color: "#22c55e",
                fontWeight: 500,
              }}>
                <span style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: "#22c55e",
                  animation: "pulse 2s infinite",
                }} />
                Live updates enabled
              </div>
            </ChartCard>
          </Col>
        </Row>
          </>
        )}

        {/* ==================== EVENTS TAB ==================== */}
        {activeTab === "events" && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} lg={16}>
                <ChartCard title="All Events" subtitle="Complete event breakdown" badge={eventBreakdown?.events?.length || 0} minHeight={500}>
                  {eventBreakdown?.events?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {eventBreakdown.events.map((event, index) => {
                        const config = getEventConfig(event.name);
                        const maxCount = Math.max(...eventBreakdown.events.map(e => e.count), 1);
                        const widthPercent = (event.count / maxCount) * 100;
                        return (
                          <div key={event.name} style={{
                            padding: "12px 14px",
                            background: index < 3 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                            border: index < 3 ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                            borderRadius: "10px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{
                                  width: "28px",
                                  height: "28px",
                                  borderRadius: "8px",
                                  background: config.color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff",
                                  fontSize: "12px"
                                }}>
                                  {config.icon}
                                </div>
                                <Text style={{ fontWeight: 600, fontSize: "13px" }}>{config.label}</Text>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontWeight: 700, color: config.color, fontSize: "14px" }}>{formatNumber(event.count)}</div>
                                <div style={{ fontSize: "10px", color: "#94a3b8" }}>{event.percentage?.toFixed(1) || 0}%</div>
                              </div>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: `linear-gradient(90deg, ${config.color} 0%, ${config.color}cc 100%)`, borderRadius: "3px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No events" />
                  )}
                </ChartCard>
              </Col>
              <Col xs={24} lg={8}>
                <ChartCard title="Event Categories" subtitle="Grouped by type" badge={eventBreakdown?.categories?.length || 0} minHeight={500}>
                  {eventBreakdown?.categories?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {eventBreakdown.categories.map((category, index) => {
                        const categoryColors = {
                          widget: "#06b6d4",
                          copilot: "#8b5cf6",
                          craft: "#f59e0b",
                          smart_reply: "#3b82f6",
                          chat: "#10b981",
                          quick_copilot: "#7c3aed",
                        };
                        const color = categoryColors[category.name] || colors[index % colors.length];
                        return (
                          <div key={category.name} style={{
                            padding: "16px",
                            background: index === 0 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                            border: index === 0 ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                            borderRadius: "12px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "8px",
                                  background: color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff",
                                  fontSize: "14px"
                                }}>
                                  <PieChartOutlined />
                                </div>
                                <div>
                                  <Text style={{ fontWeight: 600, fontSize: "14px", textTransform: "capitalize" }}>{category.name.replace(/_/g, " ")}</Text>
                                  <div style={{ fontSize: "11px", color: "#94a3b8" }}>{category.eventCount} event types</div>
                                </div>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontWeight: 700, color, fontSize: "18px" }}>{formatNumber(category.count)}</div>
                                <div style={{ fontSize: "11px", color: "#94a3b8" }}>{category.percentage?.toFixed(1) || 0}%</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No categories" />
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {/* ==================== USERS TAB ==================== */}
        {activeTab === "users" && (
          <>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <ChartCard title="Top Users" subtitle="Most active users by events" badge={userActivity?.total || 0} minHeight={400}>
                  {userActivity?.users?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {userActivity.users.map((user, index) => {
                        const timeAgo = user.lastActivity ? (() => {
                          const diff = Math.floor((Date.now() - new Date(user.lastActivity).getTime()) / 1000);
                          if (diff < 60) return `${diff}s ago`;
                          if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                          if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                          return `${Math.floor(diff / 86400)}d ago`;
                        })() : null;
                        return (
                          <div key={user.id} style={{
                            padding: "14px 16px",
                            background: index < 3 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                            border: index < 3 ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                            borderRadius: "12px",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onClick={() => history.push(`/super-admin/admin/${user.id}`)}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                                <div style={{ position: "relative" }}>
                                  <Avatar
                                    size={44}
                                    src={user.avatar}
                                    style={{
                                      background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {user.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                                  </Avatar>
                                  {index < 3 && (
                                    <div style={{
                                      position: "absolute",
                                      top: -4,
                                      right: -4,
                                      width: "18px",
                                      height: "18px",
                                      borderRadius: "50%",
                                      background: index === 0 ? "#f59e0b" : index === 1 ? "#94a3b8" : "#cd7f32",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#fff",
                                      fontSize: "10px",
                                      fontWeight: 700,
                                      border: "2px solid #fff",
                                    }}>
                                      {index + 1}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "14px" }}>
                                    {user.name}
                                  </div>
                                  <div style={{ fontSize: "12px", color: "#64748b" }}>{user.email}</div>
                                  {timeAgo && (
                                    <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>
                                      <ClockCircleOutlined style={{ marginRight: "4px", fontSize: "10px" }} />
                                      Last active {timeAgo}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                                <div style={{ textAlign: "center" }}>
                                  <div style={{ fontSize: "18px", fontWeight: 700, color: "#7c3aed" }}>{formatNumber(user.totalEvents)}</div>
                                  <div style={{ fontSize: "10px", color: "#94a3b8" }}>events</div>
                                </div>
                                <div style={{ textAlign: "center" }}>
                                  <div style={{ fontSize: "14px", fontWeight: 600, color: "#3b82f6" }}>{user.eventTypes}</div>
                                  <div style={{ fontSize: "10px", color: "#94a3b8" }}>types</div>
                                </div>
                                <div style={{
                                  padding: "4px 10px",
                                  background: user.plan === "pro" ? "#8b5cf615" : user.plan === "business" ? "#3b82f615" : "#10b98115",
                                  color: user.plan === "pro" ? "#8b5cf6" : user.plan === "business" ? "#3b82f6" : "#10b981",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  textTransform: "capitalize"
                                }}>
                                  {user.plan}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 20px" }}>
                      <TeamOutlined style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }} />
                      <div style={{ fontSize: "16px", fontWeight: 600, color: "#64748b", marginBottom: "8px" }}>No User Activity Data</div>
                      <div style={{ fontSize: "13px", color: "#94a3b8" }}>User activity tracking will show detailed per-user analytics when available.</div>
                    </div>
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {/* ==================== DEVICES TAB ==================== */}
        {activeTab === "devices" && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
              <Col xs={24} lg={12}>
                <ChartCard title="Platform Distribution" subtitle="Usage by platform" badge={platforms?.length} minHeight={350}>
                  {platforms?.length > 0 ? (
                    <>
                      <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
                        <div style={{ position: "relative", width: "180px", height: "180px" }}>
                          <svg viewBox="0 0 36 36" style={{ transform: "rotate(-90deg)", width: "100%", height: "100%" }}>
                            <circle cx="18" cy="18" r="15.915" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                            {platforms.reduce((acc, platform, index) => {
                              const percentage = totalPlatformCount > 0 ? (platform.count / totalPlatformCount) * 100 : 0;
                              const color = platformColors[platform.platform] || colors[index % colors.length];
                              const offset = acc.offset;
                              acc.elements.push(
                                <circle
                                  key={platform.platform}
                                  cx="18" cy="18" r="15.915" fill="none"
                                  stroke={color} strokeWidth="3"
                                  strokeDasharray={`${percentage} ${100 - percentage}`}
                                  strokeDashoffset={-offset}
                                  strokeLinecap="round"
                                />
                              );
                              acc.offset += percentage;
                              return acc;
                            }, { elements: [], offset: 0 }).elements}
                          </svg>
                          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                            <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e293b" }}>
                              {formatNumber(totalPlatformCount)}
                            </div>
                            <div style={{ fontSize: "11px", color: "#64748b" }}>Total</div>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {platforms.map((platform, index) => {
                          const color = platformColors[platform.platform] || colors[index % colors.length];
                          const percentage = totalPlatformCount > 0 ? ((platform.count / totalPlatformCount) * 100).toFixed(1) : 0;
                          return (
                            <div key={platform.platform} style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "10px 12px",
                              background: `${color}10`,
                              borderRadius: "10px",
                              border: `1px solid ${color}20`,
                            }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <div style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "8px",
                                  background: color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff",
                                }}>
                                  {getPlatformIcon(platform.platform)}
                                </div>
                                <span style={{ fontSize: "13px", fontWeight: 500, color: "#1e293b" }}>{platform.platform}</span>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: "14px", fontWeight: 600, color }}>{formatNumber(platform.count)}</div>
                                <div style={{ fontSize: "11px", color: "#64748b" }}>{percentage}%</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No platform data" />
                  )}
                </ChartCard>
              </Col>
              <Col xs={24} lg={12}>
                <ChartCard title="Device Types" subtitle="Desktop, Mobile, Tablet" minHeight={350}>
                  {deviceData?.deviceTypes?.length > 0 || Object.keys(deviceData || {}).length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {["desktop", "mobile", "tablet"].map((type, index) => {
                        const count = deviceData?.counts?.[type] || deviceData?.[type] || 0;
                        const deviceIcons = {
                          desktop: <DesktopOutlined />,
                          mobile: <MobileOutlined />,
                          tablet: <LaptopOutlined />,
                        };
                        const deviceColors = {
                          desktop: "#3b82f6",
                          mobile: "#10b981",
                          tablet: "#f59e0b",
                        };
                        const total = (deviceData?.counts?.desktop || deviceData?.desktop || 0) +
                                      (deviceData?.counts?.mobile || deviceData?.mobile || 0) +
                                      (deviceData?.counts?.tablet || deviceData?.tablet || 0);
                        const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                        const color = deviceColors[type];
                        return (
                          <div key={type} style={{
                            padding: "16px",
                            background: `${color}10`,
                            borderRadius: "12px",
                            border: `1px solid ${color}20`,
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <div style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "10px",
                                  background: color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff",
                                  fontSize: "18px",
                                }}>
                                  {deviceIcons[type]}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: "15px", textTransform: "capitalize" }}>{type}</div>
                                  <div style={{ fontSize: "12px", color: "#64748b" }}>{percentage}% of total</div>
                                </div>
                              </div>
                              <div style={{ fontSize: "24px", fontWeight: 700, color }}>{formatNumber(count)}</div>
                            </div>
                            <div style={{ height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                              <div style={{ width: `${percentage}%`, height: "100%", background: color, borderRadius: "4px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No device data" />
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {/* ==================== GEOGRAPHIC TAB ==================== */}
        {activeTab === "geographic" && (
          <>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <ChartCard title="Geographic Distribution" subtitle="Users by country" badge={geoData?.length || 0} minHeight={450}>
                  {geoData?.length > 0 ? (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px" }}>
                      {geoData.map((country, index) => {
                        const maxCount = Math.max(...geoData.map(c => c.count || c.users || 0), 1);
                        const count = country.count || country.users || 0;
                        const widthPercent = (count / maxCount) * 100;
                        return (
                          <div key={country.country || country._id || index} style={{
                            padding: "16px",
                            background: index < 3 ? "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)" : "#f8fafc",
                            border: index < 3 ? "1px solid #c4b5fd" : "1px solid #e2e8f0",
                            borderRadius: "12px"
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <span style={{ fontSize: "24px" }}>
                                  {country.flag || "🌍"}
                                </span>
                                <span style={{ fontWeight: 600, fontSize: "14px" }}>{country.country || country._id || "Unknown"}</span>
                              </div>
                              <div style={{ fontWeight: 700, fontSize: "16px", color: "#7c3aed" }}>
                                {formatNumber(count)}
                              </div>
                            </div>
                            <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${widthPercent}%`, height: "100%", background: "linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)", borderRadius: "3px" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 20px" }}>
                      <GlobalOutlined style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }} />
                      <div style={{ fontSize: "16px", fontWeight: 600, color: "#64748b", marginBottom: "8px" }}>No Geographic Data</div>
                      <div style={{ fontSize: "13px", color: "#94a3b8" }}>Geographic distribution will appear once events with location data are tracked.</div>
                    </div>
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}

        {/* ==================== REALTIME TAB ==================== */}
        {activeTab === "realtime" && (
          <>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <ChartCard title="Real-time Activity Feed" subtitle="Live events as they happen" minHeight={500}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "6px 12px",
                      background: "#ecfdf5",
                      borderRadius: "20px",
                      border: "1px solid #6ee7b7",
                    }}>
                      <span style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#22c55e",
                        animation: "pulse 2s infinite",
                      }} />
                      <span style={{ fontSize: "12px", fontWeight: 600, color: "#059669" }}>Live</span>
                    </div>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => refetchRealtime()}
                      style={{ borderRadius: "6px" }}
                    >
                      Refresh
                    </Button>
                  </div>
                  {realtimeEvents?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                      {realtimeEvents.map((event, index) => {
                        const config = getEventConfig(event.type);
                        const timeAgo = (() => {
                          const diff = Math.floor((Date.now() - new Date(event.timestamp).getTime()) / 1000);
                          if (diff < 60) return `${diff}s ago`;
                          if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                          if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                          return `${Math.floor(diff / 86400)}d ago`;
                        })();
                        return (
                          <div key={event.id} style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "16px",
                            padding: "16px 0",
                            borderBottom: index < realtimeEvents.length - 1 ? "1px solid #f1f5f9" : "none",
                          }}>
                            <div style={{ position: "relative" }}>
                              <Avatar
                                size={44}
                                src={event.user?.avatar}
                                style={{
                                  background: "linear-gradient(135deg, #e9d5ff 0%, #f5f3ff 100%)",
                                  color: "#7c3aed",
                                  fontWeight: 600,
                                  fontSize: "14px",
                                }}
                              >
                                {event.user?.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?"}
                              </Avatar>
                              <div style={{
                                position: "absolute",
                                bottom: -2,
                                right: -2,
                                width: "18px",
                                height: "18px",
                                borderRadius: "50%",
                                background: config.color,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#fff",
                                border: "2px solid #fff",
                                fontSize: "9px",
                              }}>
                                {config.icon}
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                                <span style={{ fontWeight: 600, fontSize: "14px", color: "#1e293b" }}>
                                  {event.user?.name || "Anonymous"}
                                </span>
                                <span style={{ fontSize: "12px", color: "#94a3b8" }}>{timeAgo}</span>
                              </div>
                              <div style={{ fontSize: "13px", color: config.color, fontWeight: 500, marginBottom: "4px" }}>
                                {config.label}
                              </div>
                              {event.metadata?.tokens > 0 && (
                                <span style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  background: "#fef3c7",
                                  padding: "3px 8px",
                                  borderRadius: "4px",
                                  color: "#d97706",
                                  fontSize: "11px",
                                  fontWeight: 500,
                                }}>
                                  <ThunderboltOutlined style={{ fontSize: "10px" }} />
                                  {event.metadata.tokens.toLocaleString()} tokens
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 20px" }}>
                      <ThunderboltOutlined style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }} />
                      <div style={{ fontSize: "16px", fontWeight: 600, color: "#64748b", marginBottom: "8px" }}>No Recent Activity</div>
                      <div style={{ fontSize: "13px", color: "#94a3b8" }}>Real-time events will appear here as users interact with the platform.</div>
                    </div>
                  )}
                </ChartCard>
              </Col>
            </Row>
          </>
        )}
      </div>

      {/* Add animation styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default AnalyticsDashboard;
